import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { flattenToAppURL } from '@plone/volto/helpers';
import VodButton from '../../../shared/VodButton';
import { UniversalLink } from '@plone/volto/components';

import {
  getPodcastsPageItems,
  normalizePodcastsPageItems,
  type PodcastsPageItem,
} from '../../../../services/api/podcastsPage';

import {
  getPodcastPrograms,
  normalizePodcastPrograms,
  type PodcastProgramFilter,
} from '../../../../services/api/podcastPrograms';
import { selectViewData, VIEW_DATA_KEYS } from '../viewSsrData';

import { AdManagerSlot } from '../../../../components/AdManagerSlot';

import './PodcastsView.less';

type PodcastsViewProps = {
  content?: {
    title?: string;
    description?: string;
  };
};

type ContinueListeningItem = {
  url: string;
  title?: string;
  subtitle?: string;
  image?: string;
};

const CONTINUE_LISTENING_KEY = 'radionacional:lastVod'; //MUDAR PRA RADIO NACIONAL

export default function PodcastsView({ content }: PodcastsViewProps) {
  const preloadedItems = useSelector((state: any) =>
    selectViewData(state, VIEW_DATA_KEYS.podcasts),
  );
  const preloadedPrograms = useSelector((state: any) =>
    selectViewData(state, VIEW_DATA_KEYS.podcastPrograms),
  );
  const hasPreloadedItems = Array.isArray(preloadedItems);
  const hasPreloadedPrograms = Array.isArray(preloadedPrograms);
  const [items, setItems] = useState<PodcastsPageItem[]>(() =>
    normalizePodcastsPageItems(preloadedItems),
  );
  const [page, setPage] = useState(0);
  const [activeProgram, setActiveProgram] = useState<PodcastProgramFilter>({
    title: 'Todos',
    token: 'Todos',
  });

  const [programs, setPrograms] = useState<PodcastProgramFilter[]>(() =>
    normalizePodcastPrograms(preloadedPrograms),
  );
  const [search, setSearch] = useState('');
  const [lastVod, setLastVod] = useState<ContinueListeningItem | null>(null);
  const [isLoading, setIsLoading] = useState(!hasPreloadedItems);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const filtersRef = useRef<HTMLDivElement | null>(null);

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const playerMode = useSelector((state: any) => state.player?.mode);
  const currentVodUrl = useSelector(
    (state: any) => state.player?.vodUrl as string | null,
  );
  const currentVodTitle = useSelector(
    (state: any) => state.player?.vodTitle as string | undefined,
  );
  const currentVodSubtitle = useSelector(
    (state: any) => state.player?.vodSubtitle as string | undefined,
  );
  const currentVodImage = useSelector(
    (state: any) => state.player?.vodImage as string | undefined,
  );
  const intentPlaying = useSelector(
    (state: any) => !!state.player?.intentPlaying,
  );

  const isPodcastPlaying = (audio?: string) =>
    playerMode === 'vod' && currentVodUrl === audio && intentPlaying;

  const loadPodcasts = useCallback(
    async (targetPage = 0, append = false) => {
      try {
        append ? setIsLoadingMore(true) : setIsLoading(true);
        setError('');

        const data = await getPodcastsPageItems({
          page: targetPage,
          search,
          program: activeProgram.token,
        });

        setHasMore(data.length > 0);

        setItems((current) => {
          if (!append) return data;

          const existingIds = new Set(current.map((item) => item['@id']));
          const nextItems = data.filter(
            (item) => !existingIds.has(item['@id']),
          );

          return [...current, ...nextItems];
        });
      } catch (err) {
        console.error('[PodcastsView] erro ao carregar podcasts:', err);
        setError('Não foi possível carregar os podcasts no momento.');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [search, activeProgram.token],
  );

  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (
      hasPreloadedItems &&
      !search.trim() &&
      activeProgram.token === 'Todos'
    ) {
      setItems(normalizePodcastsPageItems(preloadedItems));
      setPage(0);
      setHasMore(normalizePodcastsPageItems(preloadedItems).length > 0);
      setIsLoading(false);
      return;
    }

    const timeout = window.setTimeout(
      () => {
        setPage(0);
        loadPodcasts(0, false);
      },
      search.trim() ? 350 : 0,
    );

    return () => window.clearTimeout(timeout);
  }, [
    activeProgram.token,
    hasPreloadedItems,
    loadPodcasts,
    preloadedItems,
    search,
  ]);

  useEffect(() => {
    if (hasPreloadedPrograms) {
      setPrograms(normalizePodcastPrograms(preloadedPrograms));
      return;
    }

    try {
      const stored = window.localStorage.getItem(CONTINUE_LISTENING_KEY);

      if (!stored) return;

      const parsed = JSON.parse(stored) as ContinueListeningItem;

      if (parsed?.url) {
        setLastVod(parsed);
      }
    } catch (err) {
      console.warn(
        '[PodcastsView] não foi possível recuperar o último VOD:',
        err,
      );
    }
  }, [hasPreloadedPrograms, preloadedPrograms]);

  useEffect(() => {
    if (playerMode !== 'vod' || !currentVodUrl) return;

    const nextLastVod: ContinueListeningItem = {
      url: currentVodUrl,
      title: currentVodTitle,
      subtitle: currentVodSubtitle,
      image: currentVodImage,
    };

    setLastVod(nextLastVod);

    try {
      window.localStorage.setItem(
        CONTINUE_LISTENING_KEY,
        JSON.stringify(nextLastVod),
      );
    } catch (err) {
      console.warn('[PodcastsView] não foi possível salvar o último VOD:', err);
    }
  }, [
    playerMode,
    currentVodUrl,
    currentVodTitle,
    currentVodSubtitle,
    currentVodImage,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function loadProgramsFilter() {
      const data = await getPodcastPrograms();

      if (!cancelled) {
        setPrograms(data);
      }
    }

    loadProgramsFilter();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredItems = items;

  const hasActiveFilters =
    activeProgram.token !== 'Todos' || search.trim() !== '';

  const featured = filteredItems[0];

  const listItems =
    featured && !hasActiveFilters
      ? filteredItems.filter((item) => item['@id'] !== featured['@id'])
      : filteredItems;
  const continueListening = useMemo(() => {
    if (playerMode === 'vod' && currentVodUrl) {
      return {
        url: currentVodUrl,
        title: currentVodTitle,
        subtitle: currentVodSubtitle,
        image: currentVodImage,
      };
    }

    return lastVod;
  }, [
    playerMode,
    currentVodUrl,
    currentVodTitle,
    currentVodSubtitle,
    currentVodImage,
    lastVod,
  ]);

  const shouldShowContinueListening =
    !!continueListening?.url &&
    !filteredItems.some((item) => isPodcastPlaying(item.audio));

  const clearFilters = () => {
    setSearch('');
    setActiveProgram({ title: 'Todos', token: 'Todos' });
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;

    setPage(nextPage);
    loadPodcasts(nextPage, true);
  };

  const renderHeroLoading = () => (
    <section className="podcasts-view__hero podcasts-view__hero--loading">
      <div className="podcasts-view__hero-content">
        <div className="podcasts-view__loadingEyebrow shimmer" />
        <div className="podcasts-view__loadingHeroTitle shimmer" />
        <div className="podcasts-view__loadingHeroText shimmer" />
        <div className="podcasts-view__loadingHeroText podcasts-view__loadingHeroText--short shimmer" />
      </div>

      <article className="podcasts-view__featured podcasts-view__featured--loading">
        <div className="podcasts-view__loadingFeaturedImage shimmer" />

        <div className="podcasts-view__featured-content">
          <div className="podcasts-view__loadingLabel shimmer" />
          <div className="podcasts-view__loadingFeaturedTitle shimmer" />
          <div className="podcasts-view__loadingFeaturedText shimmer" />
          <div className="podcasts-view__loadingFeaturedText podcasts-view__loadingFeaturedText--short shimmer" />
          <div className="podcasts-view__loadingMeta shimmer" />
          <div className="podcasts-view__loadingButton shimmer" />
        </div>
      </article>
    </section>
  );

  const renderGridLoading = () => (
    <div className="podcasts-view__grid podcasts-view__grid--loading">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <article
          className="podcasts-view__card podcasts-view__card--loading"
          key={index}
        >
          <div className="podcasts-view__thumb podcasts-view__thumb--loading">
            <div className="podcasts-view__loadingThumb shimmer" />
          </div>

          <div className="podcasts-view__card-body">
            <div className="podcasts-view__card-meta">
              <div className="podcasts-view__loadingMetaLine shimmer" />
              <div className="podcasts-view__loadingMetaLine podcasts-view__loadingMetaLine--small shimmer" />
            </div>

            <div className="podcasts-view__loadingCardTitle shimmer" />
            <div className="podcasts-view__loadingCardText shimmer" />
            <div className="podcasts-view__loadingCardText podcasts-view__loadingCardText--short shimmer" />
            <div className="podcasts-view__loadingButton shimmer" />
          </div>
        </article>
      ))}
    </div>
  );

  const renderEqualizer = () => (
    <div className="podcasts-view__equalizer" aria-hidden="true">
      <span />
      <span />
      <span />
    </div>
  );

  const sectionTitle =
    activeProgram.token !== 'Todos' && search.trim()
      ? `${activeProgram.title}: “${search.trim()}”`
      : activeProgram.token !== 'Todos'
        ? activeProgram.title
        : search.trim()
          ? `Resultados para “${search.trim()}”`
          : 'Todos os podcasts';

  const updateFilterArrows = () => {
    const el = filtersRef.current;

    if (!el) return;

    const hasLeft = el.scrollLeft > 8;

    const hasRight = el.scrollWidth - el.clientWidth - el.scrollLeft > 8;

    setShowLeftArrow(hasLeft);
    setShowRightArrow(hasRight);
  };

  useEffect(() => {
    const el = filtersRef.current;

    if (!el) return;

    updateFilterArrows();

    el.addEventListener('scroll', updateFilterArrows);

    window.addEventListener('resize', updateFilterArrows);

    return () => {
      el.removeEventListener('scroll', updateFilterArrows);
      window.removeEventListener('resize', updateFilterArrows);
    };
  }, [programs]);

  const scrollFilters = (direction: 'left' | 'right') => {
    const el = filtersRef.current;

    if (!el) return;

    el.scrollBy({
      left: direction === 'right' ? 320 : -320,
      behavior: 'smooth',
    });
  };

  return (
    <main className="podcasts-view">
      {isLoading ? (
        renderHeroLoading()
      ) : (
        <section className="podcasts-view__hero">
          <div className="podcasts-view__hero-content">
            <div className="podcasts-view__eyebrow">
              Podcasts Rádio Nacional
            </div>

            <h1>
              Explore os{' '}
              <span style={{ color: 'var(--primary-site)' }}>podcasts</span> da
              Rádio Nacional
            </h1>

            <p>
              {content?.description ||
                'Ouça entrevistas, séries especiais, música, cultura e os principais conteúdos sonoros da Rádio Nacional.'}
            </p>
          </div>

          {featured && (
            <article
              className={
                isPodcastPlaying(featured.audio)
                  ? 'podcasts-view__featured podcasts-view__featured--active'
                  : 'podcasts-view__featured'
              }
            >
              {featured.image && (
                <img
                  src={featured.image}
                  alt=""
                  className="podcasts-view__featured-image"
                  loading="eager"
                />
              )}

              <div className="podcasts-view__featured-content">
                <div className="podcasts-view__label">
                  {isPodcastPlaying(featured.audio)
                    ? 'Tocando agora'
                    : hasActiveFilters
                      ? 'Episódio encontrado'
                      : 'Último episódio'}

                  {isPodcastPlaying(featured.audio) && renderEqualizer()}
                </div>

                <div className="podcasts-view__title">
                  <UniversalLink href={flattenToAppURL(featured['@id'])}>
                    {featured.title}
                  </UniversalLink>
                </div>

                {featured.description && <p>{featured.description}</p>}

                <div className="podcasts-view__meta">
                  {featured.programTitle && (
                    <UniversalLink
                      href={flattenToAppURL(featured.programUrl || '')}
                    >
                      {featured.programTitle}
                    </UniversalLink>
                  )}

                  {featured.effective && <div>{featured.effective}</div>}
                </div>

                {featured.audio && (
                  <div className="podcasts-view__vod">
                    <VodButton
                      url={featured.audio}
                      pageUrl={featured['@id']}
                      title={featured.title}
                      subtitle={featured.programTitle}
                      image={featured.image}
                      origin="PodcastsView"
                      className={
                        isPodcastPlaying(featured.audio)
                          ? 'vodButton--active pressable'
                          : ''
                      }
                    />
                  </div>
                )}
              </div>
            </article>
          )}
        </section>
      )}

      <AdManagerSlot
        slotKey="largeLeaderboard"
        className="ad-manager-slot--leaderboard"
      />

      <section className="podcasts-view__controls" aria-label="Filtros">
        <div className="podcasts-view__controls-header">
          <div>
            <span>Catálogo</span>
            <h2>Encontre seu próximo episódio</h2>
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              className="podcasts-view__clear"
              onClick={clearFilters}
            >
              Limpar filtros
            </button>
          )}
        </div>

        <div className="podcasts-view__search">
          <label htmlFor="podcast-search">Buscar podcast</label>

          <input
            id="podcast-search"
            type="search"
            value={search}
            placeholder="Busque por título, programa ou descrição"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="podcasts-view__filters-wrap">
          {showLeftArrow && (
            <button
              type="button"
              className="
                podcasts-view__filters-arrow
                podcasts-view__filters-arrow--left
              "
              onClick={() => scrollFilters('left')}
              aria-label="Ver programas anteriores"
            >
              <span aria-hidden="true">‹</span>
            </button>
          )}

          <div
            ref={filtersRef}
            className="podcasts-view__filters"
            aria-label="Filtrar por programa"
          >
            {programs.map((program) => (
              <button
                key={program.token}
                type="button"
                className={
                  activeProgram.token === program.token
                    ? 'podcasts-view__filter podcasts-view__filter--active'
                    : 'podcasts-view__filter'
                }
                onClick={() => setActiveProgram(program)}
              >
                {program.title}
              </button>
            ))}
          </div>

          {showRightArrow && (
            <button
              type="button"
              className="
                podcasts-view__filters-arrow
                podcasts-view__filters-arrow--right
              "
              onClick={() => scrollFilters('right')}
              aria-label="Ver mais programas"
            >
              <span aria-hidden="true">›</span>
            </button>
          )}
        </div>

        {!isLoading && !error && (
          <div className="podcasts-view__results">
            {filteredItems.length} episódio
            {filteredItems.length !== 1 ? 's' : ''} encontrado
            {filteredItems.length !== 1 ? 's' : ''}
          </div>
        )}
      </section>

      {shouldShowContinueListening && continueListening && (
        <section
          className="podcasts-view__continue"
          aria-label="Continuar ouvindo"
        >
          <div className="podcasts-view__continue-card">
            {continueListening.image && (
              <img
                src={continueListening.image}
                alt=""
                className="podcasts-view__continue-image"
                loading="lazy"
              />
            )}

            <div className="podcasts-view__continue-content">
              <span>Continuar ouvindo</span>

              <h2>{continueListening.title || 'Áudio do portal'}</h2>

              {continueListening.subtitle && (
                <p>{continueListening.subtitle}</p>
              )}
            </div>

            <div className="podcasts-view__continue-action">
              <VodButton
                url={continueListening.url}
                title={continueListening.title}
                subtitle={continueListening.subtitle}
                image={continueListening.image}
                origin="PodcastsViewContinueListening"
                label="Continuar"
                className={
                  playerMode === 'vod' &&
                  currentVodUrl === continueListening.url &&
                  intentPlaying
                    ? 'vodButton--active pressable'
                    : ''
                }
              />
            </div>
          </div>
        </section>
      )}

      <section className="podcasts-view__content">
        {!isLoading && !error && listItems.length > 0 && (
          <header className="podcasts-view__section-header">
            <div>Explore a programação sob demanda</div>
            <h2>{sectionTitle}</h2>
          </header>
        )}

        {isLoading && renderGridLoading()}

        {!isLoading && error && (
          <div className="podcasts-view__state podcasts-view__state--error">
            {error}
          </div>
        )}

        {!isLoading && !error && filteredItems.length === 0 && (
          <div className="podcasts-view__state">
            Nenhum podcast encontrado para essa busca.
          </div>
        )}

        {!isLoading && !error && listItems.length > 0 && (
          <div className="podcasts-view__grid">
            {listItems.map((item) => {
              const isActive = isPodcastPlaying(item.audio);

              return (
                <article
                  className={
                    isActive
                      ? 'podcasts-view__card podcasts-view__card--active'
                      : 'podcasts-view__card'
                  }
                  key={item['@id']}
                >
                  <UniversalLink
                    className="podcasts-view__thumb"
                    href={flattenToAppURL(item['@id'])}
                  >
                    {item.image ? (
                      <img src={item.image} alt="" loading="lazy" />
                    ) : (
                      <div className="podcasts-view__thumb-fallback">
                        {item.programTitle || 'Rádio nacional'}
                      </div>
                    )}

                    {isActive && (
                      <div className="podcasts-view__now-playing">
                        {renderEqualizer()}
                        <span>Tocando</span>
                      </div>
                    )}
                  </UniversalLink>

                  <div className="podcasts-view__card-body">
                    <div className="podcasts-view__card-meta">
                      {item.programTitle && (
                        <UniversalLink
                          href={flattenToAppURL(item.programUrl || '')}
                        >
                          {item.programTitle}
                        </UniversalLink>
                      )}

                      {item.effective && <div>{item.effective}</div>}
                    </div>

                    <h2>
                      <UniversalLink href={flattenToAppURL(item['@id'])}>
                        {item.title}
                      </UniversalLink>
                    </h2>

                    {item.description && <p>{item.description}</p>}

                    {item.audio && (
                      <div className="podcasts-view__vod">
                        <VodButton
                          url={item.audio}
                          pageUrl={item['@id']}
                          title={item.title}
                          subtitle={item.programTitle}
                          image={item.image}
                          origin="PodcastsView"
                          className={
                            isActive ? 'pressable vodButton--active' : ''
                          }
                        />
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!isLoading && !error && listItems.length > 0 && hasMore && (
          <button
            type="button"
            className="podcasts-view__load-more"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? 'Carregando...' : 'Carregar mais episódios'}
          </button>
        )}
      </section>
    </main>
  );
}
